import React, { useCallback, useEffect } from "react";
import Link from "next/link";
import { capitalizeFirstWord } from "domains/commons/misc";
import SectionReferenceImageBase from "domains/inference/components/InferenceGenerator/Sidebar/SectionReferenceImageBase";
import SelectControlNet from "domains/inference/components/InferenceGenerator/Sidebar/SelectControlNet";
import SelectInferenceType from "domains/inference/components/InferenceGenerator/Sidebar/SelectInferenceType";
import SliderControlEnd from "domains/inference/components/InferenceGenerator/Sidebar/SliderControlEnd";
import SliderInfluence from "domains/inference/components/InferenceGenerator/Sidebar/SliderInfluence";
import { INFERENCE_TYPE_WITH_SECOND_REFERENCE_IMAGE } from "domains/inference/constants/inferenceTypes";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import {
  ControlNetComposablePreset,
  controlNetComposablePresets,
  ControlNetPreset,
} from "domains/inference/enum/ControlNetPreset";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import ControlSwitch from "domains/ui/components/ControlSwitch";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  usePostDetectInferencesMutation,
} from "infra/api/generated/api";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";

import {
  Box,
  HStack,
  Image as ChakraImage,
  Spinner,
  Tag,
  VStack,
} from "@chakra-ui/react";
import { useAutoAnimate } from "@formkit/auto-animate/react";

interface SidebarSectionReferenceImageProps {}

export default function SidebarSectionReferenceImage({}: SidebarSectionReferenceImageProps) {
  const intl = useIntl();
  const {
    referenceImages,
    setReferenceImage,
    isImportingAsset,
    referenceControlNet,
    setReferenceControlNet,
    isReferenceModeMappingEnabled,
    setIsReferenceModeMapLoading,
    referenceModeMapAsset,
    setReferenceModeMapAsset,
    referenceAttn,
    setReferenceAttn,
    referenceAdain,
    setReferenceAdain,
    inferenceType,
    isInCanvas,
    ipAdapterType,
    isTexture,
  } = useInferenceCreateContext();
  const handleApiError = useHandleApiError();
  const { showLimitModal } = usePlanContext();
  const [detectImageTrigger] = usePostDetectInferencesMutation();
  const [animationRef] = useAutoAnimate();
  const { selectedProject } = useTeamContext();

  const hasSecondReferenceImage =
    INFERENCE_TYPE_WITH_SECOND_REFERENCE_IMAGE.includes(inferenceType);

  const showModeMapOnReferenceImage =
    inferenceType === "controlnet" ||
    inferenceType === "controlnet_ip_adapter" ||
    inferenceType === "controlnet_texture";

  const imageToDetect = !showModeMapOnReferenceImage
    ? referenceImages.control
    : referenceImages.reference;

  const isModeMapDetectable =
    imageToDetect &&
    controlNetComposablePresets.findIndex(
      (item) => item.value === referenceControlNet
    ) !== -1;

  const isModeMapDisplayed =
    isReferenceModeMappingEnabled &&
    isModeMapDetectable &&
    inferenceType.includes("controlnet");

  // ----------------------------------

  const getModality = useCallback(() => {
    return referenceControlNet === ControlNetComposablePreset.ILLUSION
      ? "grayscale"
      : (referenceControlNet as Exclude<
          ControlNetComposablePreset,
          ControlNetComposablePreset.ILLUSION
        >);
  }, [referenceControlNet]);

  const detectImage = useCallback(async () => {
    try {
      if (!isModeMapDisplayed || !imageToDetect) {
        setReferenceModeMapAsset(undefined);
        setIsReferenceModeMapLoading(false);
        return;
      }

      if (
        referenceModeMapAsset &&
        referenceModeMapAsset.metadata.parentId === imageToDetect.id &&
        referenceModeMapAsset.metadata.modality === getModality()
      ) {
        return;
      }

      setReferenceModeMapAsset(undefined);
      setIsReferenceModeMapLoading(true);

      const modality = getModality();
      const result = await detectImageTrigger({
        projectId: selectedProject.id,
        body: {
          image: imageToDetect.id,
          modality,
        },
      })
        .unwrap()
        .then(removeCuFromType);
      setReferenceModeMapAsset(result.asset);
      // setIsReferenceModeMapLoading will be set to false when the image is loaded
    } catch (error: any) {
      setReferenceModeMapAsset(undefined);
      setIsReferenceModeMapLoading(false);
      setReferenceControlNet(ControlNetPreset.DEPTH);
      handleApiError(error, "Error detecting mode map image", {
        quota: () => {
          if (_.get(error, "data.details.remainingSeconds")) {
            showLimitModal("planCooldown", {
              timeout: error.data.details.remainingSeconds,
              type: "detection",
            });
          } else if (_.get(error, "data.name") === "RateLimitError") {
            showLimitModal("detectionRateLimit");
          } else {
            showLimitModal("planDetections");
          }
        },
      });
    }
  }, [
    detectImageTrigger,
    getModality,
    handleApiError,
    imageToDetect,
    isModeMapDisplayed,
    referenceModeMapAsset,
    selectedProject.id,
    setIsReferenceModeMapLoading,
    setReferenceControlNet,
    setReferenceModeMapAsset,
    showLimitModal,
  ]);

  // ----------------------------------

  useEffect(() => {
    void detectImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getModality, imageToDetect, isModeMapDisplayed]);

  // ----------------------------------

  return (
    <SectionReferenceImageBase
      id="referenceImage"
      title={isInCanvas ? "Reference" : "Reference Image"}
      asset={referenceImages.reference}
      setAsset={(asset) => setReferenceImage("reference", asset)}
      guide="inference-parameter-reference-image"
      withSketch="always"
      isLoading={isImportingAsset}
      imageTag={
        isReferenceModeMappingEnabled || showModeMapOnReferenceImage
          ? intl.formatMessage({
              id: "domains.inference.inferenceGenerator.reference",
              defaultMessage: "Reference",
            })
          : intl.formatMessage({
              id: "domains.inference.inferenceGenerator.modeMap",
              defaultMessage: "Mode Map",
            })
      }
      modeMapContent={
        isModeMapDisplayed && showModeMapOnReferenceImage ? (
          <ModeMapContent
            asset={referenceModeMapAsset}
            fallbackImageSrc={referenceImages.reference?.url ?? ""}
            onLoad={() => {
              setIsReferenceModeMapLoading(false);
            }}
          />
        ) : null
      }
      additionalContent={
        <VStack ref={animationRef} w="full">
          <SelectInferenceType />
          {showModeMapOnReferenceImage && <SelectControlNet />}
          <SliderInfluence type="reference" />
          {showModeMapOnReferenceImage && <SliderControlEnd />}
          {inferenceType.includes("reference") && (
            <>
              <ControlSwitch
                title="Reference Attn"
                tooltip="Attn mode is better for foreground objects."
                isChecked={referenceAttn}
                setIsChecked={() => setReferenceAttn(!referenceAttn)}
              />
              <ControlSwitch
                title="Reference AdaIN"
                tooltip="AdaIN mode is better for details like colors and backgrounds."
                isChecked={referenceAdain}
                setIsChecked={() => setReferenceAdain(!referenceAdain)}
              />
            </>
          )}
          {hasSecondReferenceImage && (
            <Box w={`calc(100% + var(--chakra-space-12))`}>
              {inferenceType.includes("ip_adapter") && (
                <SectionReferenceImageBase
                  id="ipAdapterReferenceImage"
                  title={
                    (ipAdapterType
                      ? capitalizeFirstWord(ipAdapterType)
                      : "IP Adapter") + " Image"
                  }
                  noCollapse
                  asset={referenceImages.ipAdapter}
                  setAsset={(asset) => setReferenceImage("ipAdapter", asset)}
                  withSketch="always"
                  isLoading={isImportingAsset}
                  imageTag={
                    ipAdapterType
                      ? capitalizeFirstWord(ipAdapterType)
                      : "IP Adapter"
                  }
                  removeBackgroundTrackingExtraParams={{
                    origin: _.compact([
                      "inference ip adapter",
                      ipAdapterType,
                      "image",
                    ]).join(" "),
                  }}
                  builderSectionHeaderStyle={{
                    mt: "-12px",
                  }}
                  builderSectionContentBoxStyle={{
                    pb: 0,
                  }}
                  isPasteEnabled={!referenceImages.ipAdapter}
                  additionalContent={
                    <VStack w="full">
                      <SliderInfluence type="ipAdapter" />
                    </VStack>
                  }
                  selectLibrary={{
                    tabs: isTexture
                      ? ["texture", "image"]
                      : ["image", "texture", "skybox"],
                  }}
                />
              )}

              {inferenceType.includes("controlnet") &&
                !inferenceType.includes("ip_adapter") && (
                  <SectionReferenceImageBase
                    id="controlReferenceImage"
                    title="Control Image"
                    noCollapse
                    asset={referenceImages.control}
                    setAsset={(asset) => setReferenceImage("control", asset)}
                    withSketch="always"
                    isLoading={isImportingAsset}
                    imageTag={
                      isReferenceModeMappingEnabled
                        ? intl.formatMessage({
                            id: "domains.inference.inferenceGenerator.control",
                            defaultMessage: "Control",
                          })
                        : intl.formatMessage({
                            id: "domains.inference.inferenceGenerator.modeMap",
                            defaultMessage: "Mode Map",
                          })
                    }
                    modeMapContent={
                      isModeMapDisplayed ? (
                        <ModeMapContent
                          asset={referenceModeMapAsset}
                          fallbackImageSrc={referenceImages.control?.url ?? ""}
                          onLoad={() => {
                            setIsReferenceModeMapLoading(false);
                          }}
                        />
                      ) : null
                    }
                    removeBackgroundTrackingExtraParams={{
                      origin: "inference control image",
                    }}
                    builderSectionHeaderStyle={{
                      mt: "-12px",
                    }}
                    builderSectionContentBoxStyle={{
                      pb: 0,
                    }}
                    isPasteEnabled={!referenceImages.control}
                    additionalContent={
                      <VStack w="full">
                        <SelectControlNet />
                        <SliderInfluence type="control" />
                        <SliderControlEnd />
                      </VStack>
                    }
                    selectLibrary={{
                      tabs: isTexture
                        ? ["texture", "image"]
                        : ["image", "texture", "skybox"],
                    }}
                  />
                )}
            </Box>
          )}
        </VStack>
      }
      removeBackgroundTrackingExtraParams={{
        origin: "inference reference image",
      }}
      isPasteEnabled={
        !referenceImages.reference ||
        !!referenceImages.control ||
        !hasSecondReferenceImage
      }
      selectLibrary={{
        tabs: isTexture ? ["texture", "image"] : ["image", "texture", "skybox"],
      }}
    />
  );
}

// ------------------------------------

export function ModeMapContent({
  asset,
  fallbackImageSrc,
  onLoad,
}: {
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  fallbackImageSrc: string;
  onLoad?: () => void;
}) {
  const router = useRouter();

  return (
    <>
      <Link
        href={{
          query: {
            ...router.query,
            openAssetId: asset?.id,
          },
        }}
        style={{
          pointerEvents: asset ? "auto" : "none",
        }}
      >
        <Box pos="relative" overflow="hidden" borderRadius="md">
          <ChakraImage
            maxH="160px"
            alt="Mode map image"
            fallback={
              <Box pos="relative">
                <ChakraImage
                  maxH="160px"
                  borderRadius="md"
                  alt="Mode map image preview"
                  filter="blur(10px)"
                  src={fallbackImageSrc}
                />
                <HStack
                  pos="absolute"
                  top={0}
                  left={0}
                  justify="center"
                  w="100%"
                  h="100%"
                >
                  <Spinner />
                </HStack>
              </Box>
            }
            onLoad={onLoad}
            src={asset?.url}
          />

          <Box pos="absolute" right={2} bottom={2}>
            <Tag variant="secondary">
              <FormattedMessage
                id="domains.inference.inferenceGenerator.modeMap"
                defaultMessage="Mode Map"
              />
            </Tag>
          </Box>
        </Box>
      </Link>
    </>
  );
}
